import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { RotateCcw, ArrowRight, Gauge, Lock } from "lucide-react";
import { burnoutQuiz, otherAssessments } from "../mock/mock";
import { submitQuizResult } from "../lib/api";

const OPTIONS = [
  { label: "Never", value: 0 },
  { label: "Sometimes", value: 1 },
  { label: "Often", value: 2 },
];

export default function Assessments() {
  const [step, setStep] = useState(0); // 0..questions-1, then results
  const [answers, setAnswers] = useState({});
  const total = burnoutQuiz.questions.length;
  const done = step >= total;

  const score = Object.values(answers).reduce((a, b) => a + b, 0);
  const result = done ? burnoutQuiz.results.find((r) => score <= r.max) : null;

  const answer = (val) => {
    setAnswers((a) => ({ ...a, [step]: val }));
    setTimeout(() => setStep((s) => s + 1), 180);
  };

  const reset = () => { setStep(0); setAnswers({}); };
  const pct = Math.round((Math.min(step, total) / total) * 100);

  useEffect(() => {
    if (done && result) {
      submitQuizResult({ quiz: burnoutQuiz.title, score, label: result.label, answers }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  return (
    <div>
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-5 pt-16 pb-10 text-center">
        <p className="font-ui tracking-[0.3em] text-teal text-sm font-semibold uppercase flex items-center justify-center gap-2">
          <Gauge size={16} /> Assessments &amp; Quizzes
        </p>
        <h1 className="font-serif-display text-5xl md:text-6xl font-bold text-navy mt-4">Know where you stand.</h1>
        <p className="font-ui text-navy/70 mt-5 text-lg leading-relaxed max-w-2xl mx-auto">
          Quick, honest tools that give you a clear read on your business — and a next step you can actually take.
          Start with our most popular one below.
        </p>
      </section>

      {/* Quiz card */}
      <section className="max-w-2xl mx-auto px-5 py-8">
        <div className="bg-white rounded-2xl border overflow-hidden shadow-[0_20px_50px_-30px_rgba(22,38,63,0.4)]" style={{ borderColor: "var(--cream-dark)" }}>
          <div className="h-2 bg-cream-dark">
            <div className="h-full bg-teal transition-[width] duration-300" style={{ width: `${done ? 100 : pct}%` }} />
          </div>

          <div className="p-8 md:p-10">
            {!done ? (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-serif-display text-2xl font-bold text-navy">{burnoutQuiz.title}</h2>
                  <span className="font-ui text-sm text-navy/50">{step + 1} / {total}</span>
                </div>
                <p className="font-serif-body text-2xl text-navy leading-snug min-h-[80px]">
                  {burnoutQuiz.questions[step]}
                </p>
                <div className="grid sm:grid-cols-3 gap-3 mt-8">
                  {OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      onClick={() => answer(o.value)}
                      className="py-4 rounded-xl border-2 font-ui font-semibold text-navy hover:border-teal hover:bg-teal/5 transition-colors"
                      style={{ borderColor: "var(--cream-dark)" }}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                {step > 0 && (
                  <button onClick={() => setStep((s) => s - 1)} className="mt-6 font-ui text-sm text-navy/50 hover:text-navy transition-colors">
                    ← Previous question
                  </button>
                )}
              </>
            ) : (
              <div className="text-center">
                <p className="font-ui text-sm text-navy/50 uppercase tracking-wider">Your result</p>
                <div className="w-24 h-24 rounded-full mx-auto my-5 flex items-center justify-center" style={{ backgroundColor: `${result.color}22` }}>
                  <span className="font-serif-display text-3xl font-bold" style={{ color: result.color }}>{score}</span>
                </div>
                <h2 className="font-serif-display text-3xl font-bold" style={{ color: result.color }}>{result.label}</h2>
                <p className="font-ui text-navy/75 mt-4 leading-relaxed">{result.text}</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
                  <Link to="/work-with-us" className="inline-flex items-center justify-center gap-2 bg-navy text-cream px-6 py-3 rounded-full font-ui font-semibold hover:bg-orange transition-colors">
                    Explore Support <ArrowRight size={17} />
                  </Link>
                  <button onClick={reset} className="inline-flex items-center justify-center gap-2 border px-6 py-3 rounded-full font-ui font-semibold text-navy hover:bg-cream transition-colors" style={{ borderColor: "var(--cream-dark)" }}>
                    <RotateCcw size={16} /> Retake Quiz
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Other assessments */}
      <section className="max-w-6xl mx-auto px-5 py-16">
        <h2 className="font-serif-display text-4xl font-bold text-navy text-center mb-3">More coming soon</h2>
        <p className="font-ui text-navy/60 text-center mb-12">A growing library of interactive tools to help you make better decisions, faster.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {otherAssessments.map((a) => (
            <div key={a.title} className="bg-white rounded-2xl p-6 border relative overflow-hidden" style={{ borderColor: "var(--cream-dark)" }}>
              <span className="font-ui text-xs font-semibold text-teal uppercase tracking-wider">{a.tag}</span>
              <h3 className="font-serif-display text-xl font-semibold text-navy mt-2">{a.title}</h3>
              <p className="font-ui text-sm text-navy/55 mt-2">{a.q}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 font-ui text-xs text-navy/40"><Lock size={13} /> Coming soon</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
